import type { ClickableItem, IsDropdownItem, VisibleItem } from 'layouts/main/sp-header/navigation/models';
import { NavigationItem } from 'layouts/main/sp-header/navigation/NavigationItem';
import { FormattedMessage } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { getProductReferentialLoadingStatus } from 'state/selectors';
import {
  isAddonManagementVisible,
  isBatchWorkspaceVisible,
  isDynamicWorkspaceVisible,
  isEssentialWorkspaceVisible,
  isPostTradeVisible,
  isPriceImprovementBlotterVisible,
  isQuoteBlotterVisible,
  isScheduledQuotesBlotterVisible,
  isSPLabVisible,
  isTradeBlotterVisible,
} from 'state/selectors/common/router';
import type { AppState } from 'state/state.model';
import { getSplabUrl } from 'state/user';

export function EssentialQuoteWorkspaceItem({
  isDropdownItem,
  onClick,
  isVisible,
}: IsDropdownItem & ClickableItem & VisibleItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      category="Workspace"
      targetRoute="/essential"
      action="Essential workspace"
      label="appHeader.laptop.menu.quote.essential"
      dataE2eSuffix="essential-screen"
      defaultMessage="Essential workspace"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
      isNewItem
    />
  );
}

export const ConnectedEssentialQuoteWorkspaceItem = connect((state: AppState) => ({
  isVisible: isEssentialWorkspaceVisible(state),
}))(EssentialQuoteWorkspaceItem);

export function StaticQuoteWorkspaceItem({ isDropdownItem, onClick }: IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      category="Workspace"
      targetRoute="/static"
      action="New static screen"
      label="appHeader.laptop.menu.quote.static"
      dataE2eSuffix="new-static-screen"
      defaultMessage="Quote workspace"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}

export function DynamicQuoteWorkspaceItem({
  isVisible,
  isDropdownItem,
  onClick,
}: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      category="Workspace"
      targetRoute="/dynamic"
      action="New dynamic screen"
      label="appHeader.laptop.menu.quote.dynamic"
      dataE2eSuffix="new-dynamic-screen"
      defaultMessage="New dynamic screen"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}
export const ConnectedDynamicQuoteWorkspaceItem = connect((state: AppState) => ({
  isVisible: isDynamicWorkspaceVisible(state),
}))(DynamicQuoteWorkspaceItem);

export function BatchQuoteWorkspaceItem({
  isVisible,
  isDropdownItem,
  onClick,
}: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      category="Workspace"
      targetRoute="/batch"
      action="New dynamic screen"
      label="appHeader.laptop.menu.quote.batch"
      dataE2eSuffix="new-batch-pricer"
      defaultMessage="New batch screen"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}
export const ConnectedBatchQuoteWorkspaceItem = connect((state: AppState) => ({
  isVisible: isBatchWorkspaceVisible(state) && getProductReferentialLoadingStatus(state, 'newBatch') === 'loaded',
}))(BatchQuoteWorkspaceItem);

export function PostTradeWorkspaceItem({
  isVisible,
  isDropdownItem,
  onClick,
}: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      targetRoute="/post-trade"
      label="appHeader.laptop.menu.quote.postTrade"
      isDropdownItem={isDropdownItem}
      dataE2eSuffix="postTrade"
      onClick={onClick}
    />
  );
}

export const ConnectedPostTradeWorkspaceItem = connect((state: AppState) => ({
  isVisible: isPostTradeVisible(state),
}))(PostTradeWorkspaceItem);

export function AddonManagementWorkspaceItem({
  isVisible,
  isDropdownItem,
  onClick,
}: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      targetRoute="/addon-management"
      label="appHeader.laptop.menu.quote.addonManagement"
      dataE2eSuffix="addonManagement"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}

export const ConnectedScheduledQuotesBlotterWorkspaceItem = connect((state: AppState) => ({
  isVisible: isScheduledQuotesBlotterVisible(state),
}))(ScheduledQuotesBlotterWorkspaceItem);
export function ScheduledQuotesBlotterWorkspaceItem({
  isVisible,
  isDropdownItem,
  onClick,
}: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      targetRoute="/scheduled-quotes-blotter"
      category="Workspace"
      action="Scheduled quotes blotter"
      label="appHeader.laptop.menu.quote.scheduledQuotes"
      dataE2eSuffix="scheduled-quotes"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}

export const ConnectedAddonManagementWorkspaceItem = connect((state: AppState) => ({
  isVisible: isAddonManagementVisible(state),
}))(AddonManagementWorkspaceItem);

export function WebPriceItem({ onClick, isVisible, isDropdownItem }: VisibleItem & ClickableItem & IsDropdownItem) {
  const splabUrl = useSelector((state: AppState) => getSplabUrl(state)) || '#';

  return (
    <NavigationItem
      isVisible={isVisible}
      href={splabUrl}
      target="_blank"
      dataE2eSuffix="web-pricer"
      onClick={onClick}
      isDropdownItem={isDropdownItem}
      shouldNavigate={false}
      category="Workspace"
      action="SG Markets bespoke"
    >
      <>
        <FormattedMessage id="appHeader.laptop.menu.quote.sgmeWebpricer" />
        <i className="icon icon-sm ms-2">launch</i>
      </>
    </NavigationItem>
  );
}

export const ConnectedWebPriceItem = connect((state: AppState) => ({
  isVisible: isSPLabVisible(state),
}))(WebPriceItem);

export function UnderlyingUniverseItem({ isDropdownItem, onClick }: IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      targetRoute="/underlying-universe"
      label="appHeader.laptop.menu.quote.underlyingUniverse"
      dataE2eSuffix="underlying"
      category="Workspace"
      isDropdownItem={isDropdownItem}
      action="Underlying universe"
      onClick={onClick}
    />
  );
}

export function QuoteBlotterItem({ isVisible, isDropdownItem, onClick }: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      targetRoute="/quote-blotter"
      label="appHeader.laptop.menu.quote.quotesBlotter"
      dataE2eSuffix="quote-blotter"
      category="Workspace"
      action="My quotes blotter"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}

export const ConnectedQuoteBlotterItem = connect((state: AppState) => ({
  isVisible: isQuoteBlotterVisible(state),
}))(QuoteBlotterItem);

export function TradeBlotterItem({ isVisible, isDropdownItem, onClick }: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      targetRoute="/trade-blotter"
      label="appHeader.laptop.menu.quote.tradesBlotter"
      dataE2eSuffix="trade-blotter"
      category="Workspace"
      action="My trades blotter"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}

export const ConnectedTradeBlotterItem = connect((state: AppState) => ({
  isVisible: isTradeBlotterVisible(state),
}))(TradeBlotterItem);

export function PriceImprovementBlotterItem({
  isVisible,
  isDropdownItem,
  onClick,
}: VisibleItem & IsDropdownItem & ClickableItem) {
  return (
    <NavigationItem
      isVisible={isVisible}
      targetRoute="/price-improvement-blotter"
      label="appHeader.laptop.menu.quote.priceImprovementBlotter"
      dataE2eSuffix="improvement-blotter"
      category="Workspace"
      action="Price improvement blotter"
      isDropdownItem={isDropdownItem}
      onClick={onClick}
    />
  );
}

export const ConnectedPriceImprovementBlotterItem = connect((state: AppState) => ({
  isVisible: isPriceImprovementBlotterVisible(state),
}))(PriceImprovementBlotterItem);
